import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles,
} from '@material-ui/core';
import { DialogBox } from '@mummssoftware/common-ui';
import {
  DEFAULT_CERT_FONT_SIZE,
  printBuffer,
} from '@mummssoftware/utils/api/Printing';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const CERT_FONT_SIZE_OPTIONS = [
  '18px',
  '17px',
  '16px',
  '15px',
  '14px',
  '13px',
  '12px',
  '11px',
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(1),
    flexShrink: 0,
  },
  viewer: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  pageWrap: {
    marginBottom: theme.spacing(2),
    '&:last-child': { marginBottom: 0 },
  },
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    minHeight: 200,
  },
}));

// Pure: response array → Blob for react-pdf display
const pdfResponseToBlob = (responseArr) =>
  new Blob([new Uint8Array(responseArr)], { type: 'application/pdf' });

/**
 * Owns the fetch-display-retry loop.
 * Each fontSize change triggers a new render request; the Blob and raw response
 * update in lock-step. Changing setFontSize naturally queues the next iteration,
 * so infinite font size adjustments compose without additional wiring.
 */
const useCertPdfState = (fetchCertPdf, initialFontSize) => {
  const [fontSize, setFontSize] = useState(initialFontSize);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCertPdf(fontSize)
      .then((response) => {
        if (cancelled) return;
        setRawResponse(response);
        setPdfBlob(pdfResponseToBlob(response));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fontSize]); // eslint-disable-line react-hooks/exhaustive-deps

  return { fontSize, setFontSize, pdfBlob, rawResponse, loading };
};

const CertPdfPages = ({ blob }) => {
  const classes = useStyles();
  const [numPages, setNumPages] = useState(null);
  const pageWidth = Math.min(880, window.innerWidth - 48);

  return (
    <Document
      file={blob}
      onLoadSuccess={({ numPages: n }) => setNumPages(n)}
      loading={null}
      error={<span>Error rendering PDF</span>}
    >
      {numPages != null &&
        Array.from({ length: numPages }, (_, i) => (
          <div key={i + 1} className={classes.pageWrap}>
            <Page pageNumber={i + 1} width={pageWidth} />
          </div>
        ))}
    </Document>
  );
};

const CertFontSizeToolbar = ({
  fontSize,
  onFontSizeChange,
  onFinish,
  finishDisabled,
  loading,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.toolbar}>
      <FormControl variant="outlined" size="small">
        <InputLabel id="cert-font-size-label">Font size</InputLabel>
        <Select
          labelId="cert-font-size-label"
          value={fontSize}
          label="Font size"
          onChange={(e) => onFontSizeChange(e.target.value)}
          disabled={loading}
        >
          {CERT_FONT_SIZE_OPTIONS.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        onClick={onFinish}
        disabled={finishDisabled}
      >
        Print
      </Button>
    </div>
  );
};

/**
 * Dialog that renders a certification PDF in-UI and allows the user to
 * adjust the font size before sending to the browser print dialog.
 *
 * The lifecycle is naturally composable: every font size change triggers
 * useCertPdfState's effect, re-fetches the PDF, and updates the preview —
 * the loop repeats until the user clicks Finish.
 */
export const CertPrintDialog = ({
  certForm,
  patientHeader,
  printingService,
  onClose,
}) => {
  const classes = useStyles();

  const fetchCertPdf = useCallback(
    (fontSize) =>
      printingService.printCertifications(
        [certForm],
        patientHeader,
        certForm.complete,
        undefined,
        fontSize,
      ),
    [printingService, certForm, patientHeader],
  );

  const {
    fontSize,
    setFontSize,
    pdfBlob,
    rawResponse,
    loading,
  } = useCertPdfState(fetchCertPdf, DEFAULT_CERT_FONT_SIZE);

  const handleFinish = () => {
    if (rawResponse) printBuffer(rawResponse);
    onClose();
  };

  return (
    <DialogBox
      title="Print Certifications"
      disableBackdropClick
      open
      handleClick={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          height: 'calc(100vh - 48px)',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      contentStyle={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className={classes.root}>
        <CertFontSizeToolbar
          fontSize={fontSize}
          onFontSizeChange={setFontSize}
          onFinish={handleFinish}
          finishDisabled={loading || !rawResponse}
          loading={loading}
        />
        {loading ? (
          <div className={classes.loadingOverlay}>
            <CircularProgress />
          </div>
        ) : (
          <div className={classes.viewer}>
            {pdfBlob ? (
              <CertPdfPages blob={pdfBlob} />
            ) : (
              <span>
                Error loading certification PDF. Please close and try again.
              </span>
            )}
          </div>
        )}
      </div>
    </DialogBox>
  );
};
